// ============================================================
//  MODULO: generarOrdenTrabajoPDF
// ============================================================

import jsPDF from 'jspdf';

export const generarOrdenTrabajoPDF = (
    datosGenerales,
    refacciones,
    servicios,
    calculosOrden,
    incluirIVA,
    incluirISR
) => {

    try {
        const doc = new jsPDF();

        // ============================================================
        //  CONFIGURACIÓN GENERAL
        // ============================================================
        const colors = {
            primary: [15, 23, 42],
            secondary: [30, 41, 59],
            accent: [6, 182, 212],
            light: [248, 250, 252],
            border: [226, 232, 240],
            text: [15, 23, 42],
        };

        const margin = 20;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const contentWidth = pageWidth - (2 * margin);

        // ============================================================
        //  ENCABEZADO PROFESIONAL
        // ============================================================
        const drawHeader = () => {
            doc.setFillColor(...colors.primary);
            doc.rect(0, 0, pageWidth, 50, 'F');

            doc.setFillColor(...colors.accent);
            doc.rect(0, 0, pageWidth, 4, 'F');

            doc.setFontSize(20);
            doc.setTextColor(...colors.light);
            doc.setFont('helvetica', 'bold');
            doc.text('ELEKTRONIKS', pageWidth / 2, 20, { align: 'center' });

            doc.setFontSize(10);
            doc.setTextColor(...colors.accent);
            doc.setFont('helvetica', 'normal');
            doc.text(
                'SERVICIO DE MANTENIMIENTO Y REPARACIÓN DE CAMIONES PESADOS',
                pageWidth / 2,
                27,
                { align: 'center' }
            );

            doc.setFontSize(8);
            doc.setTextColor(180, 180, 180);
            doc.text(
                'Av. de los Maestros 2206 • Aguascalientes, Ags • C.P. 20235',
                pageWidth / 2,
                34,
                { align: 'center' }
            );

            doc.text(
                'Cel: 449 205 1854 • elektroniks-@outlook.com',
                pageWidth / 2,
                38,
                { align: 'center' }
            );
        };

        // ============================================================
        //  CONTROL DE SALTO DE PÁGINA
        // ============================================================
        const checkPageBreak = (currentY, neededSpace = 10) => {
            if (currentY > pageHeight - margin - neededSpace) {
                doc.addPage();
                // En nuevas páginas no repetimos encabezado visual,
                // pero respetamos margen superior.
                return margin;
            }
            return currentY;
        };

        // ============================================================
        //  SECCIÓN (título + contenido)
        // ============================================================
        const drawSection = (title, y, contentCallback) => {
            y = checkPageBreak(y, 20);

            doc.setFillColor(...colors.secondary);
            doc.rect(margin, y, contentWidth, 8, 'F');

            doc.setFontSize(10);
            doc.setTextColor(...colors.light);
            doc.setFont('helvetica', 'bold');
            doc.text(title, margin + 5, y + 5.5);

            let newY = y + 12;
            if (contentCallback) newY = contentCallback(newY);

            return newY;
        };

        // ============================================================
        //  TABLA DE ITEMS (SERVICIOS / REFACCIONES)
        //  Diseño tipo "reporte profesional" + columna IMPORTE
        // ============================================================
        const drawItemsTable = (items, title, startY) => {
            if (!items || items.length === 0) return startY;

            let currentY = startY;

            const cantidadWidth = 20;
            const importeWidth = 35;
            const descripcionWidth = contentWidth - cantidadWidth - importeWidth - 10;

            const descX = margin + cantidadWidth + 5;
            const importeColumnX = margin + cantidadWidth + descripcionWidth + 10;
            const importeCenterX = importeColumnX + importeWidth / 2;

            // =========================
            //  ENCABEZADO DE TABLA
            // =========================
            currentY = checkPageBreak(currentY, 12);

            doc.setFillColor(240, 240, 240);
            doc.rect(margin, currentY, contentWidth, 6, 'F');

            doc.setFontSize(8);
            doc.setTextColor(...colors.text);
            doc.setFont('helvetica', 'bold');

            // CANTIDAD centrado
            const headerCantidad = 'CANT.';
            const headerCantidadWidth = doc.getTextWidth(headerCantidad);
            const cantidadHeaderX = margin + (cantidadWidth - headerCantidadWidth) / 2;
            doc.text(headerCantidad, cantidadHeaderX, currentY + 4);

            // DESCRIPCIÓN
            doc.text('DESCRIPCIÓN', descX, currentY + 4);

            // IMPORTE centrado en su columna
            const headerImporte = 'IMPORTE';
            doc.text(headerImporte, importeCenterX, currentY + 4, {
                align: 'center'
            });

            currentY += 7;

            // =========================
            //  FILAS
            // =========================
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');

            const lineHeight = 3.5;

            items.forEach((item) => {
                // Descripción compatible con servicios y refacciones
                const descripcion =
                    item.descripcion ||
                    item.nombre ||
                    item.name ||
                    'Artículo sin descripción';

                const descriptionLines = doc.splitTextToSize(
                    descripcion,
                    descripcionWidth - 5
                );

                const textBlockHeight = descriptionLines.length * lineHeight;
                const rowHeight = Math.max(8, textBlockHeight + 1);

                // Salto de página si ya no cabe el renglón
                currentY = checkPageBreak(currentY, rowHeight + 5);

                // -------- IMPORTE (numérico) --------
                let importeNum = 0;

                if (typeof item.total === 'number') {
                    // Caso habitual en tu app: viene de useBudgetItems
                    importeNum = item.total;
                } else if (typeof item.importe === 'number') {
                    importeNum = item.importe;
                } else {
                    const cantidad = Number(item.cantidad) || 0;
                    const precioUnitario =
                        Number(item.precioUnitario ?? item.precio ?? 0);
                    importeNum = cantidad * precioUnitario;
                }

                const importeTexto = importeNum.toLocaleString('es-MX', {
                    minimumFractionDigits: 2
                });

                const rowMiddleY = currentY + rowHeight / 2;

                // Cantidad centrada verticalmente
                doc.text(
                    (item.cantidad ?? '').toString(),
                    margin + cantidadWidth / 2,
                    rowMiddleY,
                    { align: 'center', baseline: 'middle' }
                );

                // Descripción en bloque, centrada verticalmente
                const blockStartY = currentY + (rowHeight - textBlockHeight) / 2;

                descriptionLines.forEach((line, i) => {
                    const lineY = blockStartY + i * lineHeight + lineHeight / 2;
                    doc.text(line, descX, lineY, { baseline: 'middle' });
                });

                // Importe CENTRADO en la misma columna que el encabezado
                doc.text(
                    importeTexto,
                    importeCenterX,
                    rowMiddleY,
                    { align: 'center', baseline: 'middle' }
                );

                // Línea separadora inferior
                doc.setDrawColor(...colors.border);
                doc.line(
                    margin,
                    currentY + rowHeight,
                    margin + contentWidth,
                    currentY + rowHeight
                );

                currentY += rowHeight;
            });

            return currentY + 2;
        };

        // ============================================================
        //  COMENZAR DOCUMENTO
        // ============================================================
        drawHeader();
        let yPosition = 55;

        // ============================================================
        //  FORMATEAR FECHA (simple, sin zona horaria)
        // ============================================================
        const formatearFecha = (iso) => {
            if (!iso) return 'N/A';

            const [yyyy, mm, dd] = iso.split('-');
            if (!yyyy || !mm || !dd) return 'N/A';

            return `${dd}/${mm}/${yyyy}`;
        };

        const fechaFormateada = formatearFecha(datosGenerales.fecha);

        // ============================================================
        //  TÍTULO PRINCIPAL 
        // ============================================================
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.text);
        doc.text('ORDEN DE TRABAJO', margin, yPosition + 5);

        yPosition += 13;

        // FOLIO y FECHA alineados
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('FOLIO:', margin, yPosition);

        doc.setFont('helvetica', 'normal');
        doc.text(datosGenerales.factura || 'N/A', margin + 15, yPosition);

        doc.setFont('helvetica', 'bold');
        doc.text('FECHA:', margin + contentWidth - 40, yPosition);

        doc.setFont('helvetica', 'normal');
        doc.text(fechaFormateada, margin + contentWidth - 25, yPosition);

        yPosition += 8;

        // ============================================================
        //  INFORMACIÓN GENERAL
        // ============================================================
        yPosition = drawSection('INFORMACIÓN', yPosition, (y) => {
            let currentY = y + 5;

            doc.setFontSize(9);
            doc.setTextColor(...colors.text);

            // Labels izquierda
            doc.setFont('helvetica', 'bold');
            doc.text('CLIENTE:', margin + 5, currentY);
            doc.text('UNIDAD:', margin + 5, currentY + 5);
            doc.text('MOTOR:', margin + 5, currentY + 10);
            doc.text('MODELO:', margin + 5, currentY + 15);

            // Labels derecha
            doc.text('VIN:', margin + 90, currentY);
            doc.text('PLACAS:', margin + 90, currentY + 5);
            doc.text('KILOMETRAJE:', margin + 90, currentY + 10);

            // Valores izquierda
            doc.setFont('helvetica', 'normal');
            doc.text(datosGenerales.cliente || 'N/A', margin + 25, currentY);
            doc.text(datosGenerales.unidad || 'N/A', margin + 25, currentY + 5);
            doc.text(datosGenerales.motor || 'N/A', margin + 25, currentY + 10);
            doc.text(datosGenerales.modelo || 'N/A', margin + 25, currentY + 15);

            // Valores derecha
            doc.text(datosGenerales.vin || 'N/A', margin + 115, currentY);
            doc.text(datosGenerales.placas || 'N/A', margin + 115, currentY + 5);
            doc.text(
                `${datosGenerales.kilometraje || 'N/A'} km`,
                margin + 115,
                currentY + 10
            );

            return currentY + 22;
        });

        // ============================================================
        //  SERVICIOS
        // ============================================================
        if (servicios && servicios.length > 0) {
            yPosition = drawSection('SERVICIOS', yPosition, (y) => {
                const startY = y + 3;
                const endY = drawItemsTable(servicios, 'servicios', startY);
                return endY + 3;
            });
        }

        // ============================================================
        //  REFACCIONES
        // ============================================================
        if (refacciones && refacciones.length > 0) {
            yPosition = drawSection('REFACCIONES', yPosition, (y) => {
                const startY = y + 3;
                const endY = drawItemsTable(refacciones, 'refacciones', startY);
                return endY + 3;
            });
        }

        // ============================================================
        //  TOTALES
        // ============================================================
        if (
            (servicios && servicios.length > 0) ||
            (refacciones && refacciones.length > 0)
        ) {

            yPosition = drawSection('TOTALES', yPosition, (y) => {
                let currentY = y;

                doc.setFontSize(9);
                doc.setTextColor(...colors.text);
                doc.setFont('helvetica', 'bold');

                // SUBTOTAL
                doc.text('SUBTOTAL', margin + 5, currentY);
                doc.text(
                    calculosOrden.subtotal.toLocaleString('es-MX', {
                        minimumFractionDigits: 2
                    }),
                    margin + contentWidth - 5,
                    currentY,
                    { align: 'right' }
                );

                currentY += 6;

                // IVA
                if (incluirIVA) {
                    doc.text('IVA', margin + 5, currentY);
                    doc.text(
                        calculosOrden.iva.toLocaleString('es-MX', {
                            minimumFractionDigits: 2
                        }),
                        margin + contentWidth - 5,
                        currentY,
                        { align: 'right' }
                    );
                    currentY += 6;
                }

                // ISR
                if (incluirISR) {
                    doc.text('ISR', margin + 5, currentY);
                    doc.text(
                        calculosOrden.isr.toLocaleString('es-MX', {
                            minimumFractionDigits: 2
                        }),
                        margin + contentWidth - 5,
                        currentY,
                        { align: 'right' }
                    );
                    currentY += 6;
                }

                // Línea divisoria
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.5);
                doc.line(margin + 5, currentY, margin + contentWidth - 5, currentY);

                currentY += 8;

                // TOTAL
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.text('TOTAL', margin + 5, currentY);
                doc.text(
                    calculosOrden.total.toLocaleString('es-MX', {
                        minimumFractionDigits: 2
                    }),
                    margin + contentWidth - 5,
                    currentY,
                    { align: 'right' }
                );

                return currentY + 10;
            });
        }

        // ============================================================
        //  OBSERVACIONES
        // ============================================================
        if (datosGenerales.observaciones) {
            yPosition = drawSection('OBSERVACIONES', yPosition, (y) => {

                doc.setFontSize(9);
                doc.setTextColor(...colors.text);
                doc.setFont('helvetica', 'normal');

                const obsLines = doc.splitTextToSize(
                    datosGenerales.observaciones,
                    contentWidth - 10
                );

                doc.text(obsLines, margin + 5, y);
                return y + (obsLines.length * 4) + 5;
            });
        }

        // ============================================================
        //  FOOTER
        // ============================================================
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        doc.text(
            'Documento generado automáticamente por el Software de ELEKTRONIKS',
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
        );

        // ============================================================
        //  NOMBRE DEL ARCHIVO
        // ============================================================
        const fileName =
            `OrdenTrabajo_${datosGenerales.factura || 'S/N'}_${fechaFormateada.replace(/\//g, '-')}.pdf`;

        // ============================================================
        //  ABRIR EN PESTAÑA (SIN FORZAR DESCARGA)
        // ============================================================
        if (typeof window !== 'undefined') {
            doc.output('dataurlnewwindow', fileName);
            return true;
        }

        // Fallback por si algún día se usa fuera del navegador
        doc.save(fileName);
        return true;

    } catch (err) {
        console.error('Error al generar PDF:', err);
        throw new Error(`Error al generar el PDF: ${err.message || err}`);
    }
};
